// frontend/src/features/livestock/AddLivestockModal.tsx

import { useState, useEffect } from 'react';
import { Modal, TextInput, NumberInput, Select, Button, Stack, Group, Text, Checkbox } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import type { NotificationData } from '@mantine/notifications';
import { useCreateLivestock } from '@/hooks/useLivestock';
import { SpeciesAutocomplete } from './SpeciesAutocomplete';
import { CURRENCIES } from '@/types/livestock';
import { CORAL_CATEGORY_OPTIONS, getSubcategoriesForCategory } from '@/data/coralCategories';
import type { FishSpeciesSearchResult, Currency, Livestock } from '@/types/livestock';

interface AddLivestockModalProps {
  opened: boolean;
  onClose: () => void;
  /** Called with the livestock just created. */
  onSuccess: (created?: Livestock) => void;
  tankId: string;  // Reserved for future use when modal manages its own tank context
  initialSearch?: string;  // Pre-fill the species search (fish) or the common name (other types)
  defaultType?: import('@/types/livestock').LivestockType | null;
  /** Acquisition date to start from, YYYY-MM-DD (e.g. a photo's date); today if not given. */
  initialAcquisitionDate?: string | null;
  /** Where the added/failed notices show (default: the app's, bottom right). */
  notificationPosition?: NotificationData['position'];
  /** false leaves out the "added" notice, for a caller that shows the new organism itself. */
  successNotice?: boolean;
  /** Fill the screen (phones). */
  fullScreen?: boolean;
}

const today = (): string => new Date().toISOString().slice(0, 10);

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function AddLivestockModal({ opened, onClose, onSuccess, tankId: _tankId, initialSearch = '', defaultType, initialAcquisitionDate, notificationPosition, successNotice = true, fullScreen = false }: AddLivestockModalProps) {
  const [commonName, setCommonName] = useState('');
  const [scientificName, setScientificName] = useState<string | null>(null);
  const [speciesId, setSpeciesId] = useState<number | null>(null);
  const [category, setCategory] = useState<string | null>(null);
  const [subcategory, setSubcategory] = useState<string | null>(null);
  const [acquisitionDate, setAcquisitionDate] = useState<string | null>(
    initialAcquisitionDate || today()
  );
  const [quantity, setQuantity] = useState(1);
  const [vendor, setVendor] = useState('');
  const [pricePaid, setPricePaid] = useState<number | string>('');
  const [currency, setCurrency] = useState<string>('USD');
  const [tankBred, setTankBred] = useState(false);
  const [livestockType, setLivestockType] = useState<string>(
    defaultType || 'fish',
  );

  const createMutation = useCreateLivestock({
    ...(notificationPosition && { notificationPosition }),
    ...(!successNotice && { successNotice: false }),
  });

  // Reset form when modal closes; start from the given name and date when it opens
  useEffect(() => {
    if (!opened) {
      resetForm();
    } else {
      setLivestockType(defaultType || 'fish');
      // Fish names come from the species search, which takes initialSearch itself
      if (initialSearch && (defaultType || 'fish') !== 'fish') setCommonName(initialSearch);
      setAcquisitionDate(initialAcquisitionDate || today());
    }
    // resetForm is redefined every render; it reads only setters and the props listed here
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, defaultType, initialSearch, initialAcquisitionDate]);

  const handleSpeciesSelect = (species: FishSpeciesSearchResult | { common_name: string; scientific_name: null; category: string | null }) => {
    setCommonName(species.common_name);
    setScientificName(species.scientific_name);
    setCategory(species.category ?? null);
    if ('id' in species) {
      setSpeciesId(species.id);
    } else {
      setSpeciesId(null);
    }
  };

  const handleSubmit = () => {
    if (!commonName || !acquisitionDate) return;

    createMutation.mutate({
      livestock_type: livestockType as import('@/types/livestock').LivestockType,
      fish_species_id: livestockType === 'fish' ? speciesId : null,
      common_name: commonName,
      scientific_name: scientificName ?? undefined,
      category: category ?? undefined,
      subcategory: subcategory ?? undefined,
      acquisition_date: acquisitionDate,
      quantity,
      vendor: vendor || undefined,
      price_paid: typeof pricePaid === 'number' ? pricePaid : undefined,
      currency: currency as Currency,
      tank_bred: tankBred,
    }, {
      onSuccess: (created) => {
        resetForm();
        onSuccess(created);
      }
    });
  };

  const resetForm = () => {
    setCommonName('');
    setScientificName(null);
    setSpeciesId(null);
    setCategory(null);
    setSubcategory(null);
    setAcquisitionDate(initialAcquisitionDate || today());
    setQuantity(1);
    setVendor('');
    setPricePaid('');
    setCurrency('USD');
    setTankBred(false);
    setLivestockType(defaultType || 'fish');
  };

  return (
    <Modal opened={opened} onClose={onClose} title={`Add ${livestockType === 'fish' ? 'Livestock' : livestockType.charAt(0).toUpperCase() + livestockType.slice(1)}`} size="md" fullScreen={fullScreen} closeButtonProps={fullScreen ? { size: 44 } : undefined}>
      <Stack>
        <Select
          label="Type"
          data={[
            { value: 'fish', label: 'Fish' },
            { value: 'coral', label: 'Coral' },
            { value: 'invertebrate', label: 'Invertebrate' },
            { value: 'plant', label: 'Plant' },
          ]}
          value={livestockType}
          onChange={(v) => {
            const type = v || 'fish';
            setLivestockType(type);
            setSubcategory(null);
            if (type !== 'fish') {
              setSpeciesId(null);
              setScientificName(null);
              // Leaving the fish species search: the name searched for becomes the name
              if (!commonName && initialSearch) setCommonName(initialSearch);
            }
          }}
        />

        {livestockType === 'fish' && (
          <SpeciesAutocomplete onSelect={handleSpeciesSelect} initialValue={initialSearch} />
        )}

        {livestockType !== 'fish' && (
          <>
            <TextInput
              label="Common Name"
              placeholder={livestockType === 'coral' ? 'e.g., Hammer Coral, Montipora' : 'Common name'}
              value={commonName}
              onChange={(e) => setCommonName(e.currentTarget.value)}
              required
            />

            {livestockType === 'coral' ? (
              <>
                <Select
                  label="Category"
                  placeholder="Select category"
                  data={CORAL_CATEGORY_OPTIONS}
                  value={category}
                  onChange={(val) => {
                    setCategory(val);
                    setSubcategory(null);
                  }}
                  searchable
                  clearable
                />
                {category && (
                  <Select
                    label="Subcategory"
                    placeholder="Select subcategory"
                    data={getSubcategoriesForCategory(category)}
                    value={subcategory}
                    onChange={setSubcategory}
                    searchable
                    clearable
                  />
                )}
                <TextInput
                  label="Scientific Name (optional)"
                  placeholder="e.g., Euphyllia ancora"
                  value={scientificName || ''}
                  onChange={(e) => setScientificName(e.currentTarget.value || null)}
                />
              </>
            ) : (
              <>
                <TextInput
                  label="Scientific Name"
                  placeholder="e.g., Euphyllia ancora"
                  value={scientificName || ''}
                  onChange={(e) => setScientificName(e.currentTarget.value || null)}
                />
                <TextInput
                  label="Category"
                  placeholder="Category"
                  value={category || ''}
                  onChange={(e) => setCategory(e.currentTarget.value || null)}
                />
              </>
            )}
          </>
        )}

        {livestockType === 'fish' && commonName && (
          <>
            <TextInput
              label="Common Name"
              value={commonName}
              onChange={(e) => setCommonName(e.currentTarget.value)}
              required
            />
            {scientificName && (
              <Text size="sm" c="dimmed" fs="italic" mt={-8}>
                {scientificName}
              </Text>
            )}
          </>
        )}

        <DateInput
          label="Acquisition Date"
          value={acquisitionDate}
          onChange={setAcquisitionDate}
          required
        />

        <NumberInput
          label="Quantity"
          value={quantity}
          onChange={(v) => setQuantity(typeof v === 'number' ? v : 1)}
          min={1}
        />

        <TextInput
          label="Vendor"
          placeholder="e.g., LiveAquaria, Local Fish Store"
          value={vendor}
          onChange={(e) => setVendor(e.currentTarget.value)}
        />

        <Checkbox
          label="Tank bred"
          checked={tankBred}
          onChange={(e) => setTankBred(e.currentTarget.checked)}
        />

        <Group grow>
          <NumberInput
            label="Price Paid"
            placeholder="0.00"
            value={pricePaid}
            onChange={setPricePaid}
            decimalScale={2}
            min={0}
          />
          <Select
            label="Currency"
            data={CURRENCIES.map(c => ({ value: c.code, label: `${c.symbol} ${c.code}` }))}
            value={currency}
            onChange={(v) => setCurrency(v || 'USD')}
          />
        </Group>

        <Group justify="flex-end" mt="md">
          {/* Full screen (a phone's assign view): 44 px targets */}
          <Button variant="subtle" onClick={onClose} h={fullScreen ? 44 : undefined}>Cancel</Button>
          <Button onClick={handleSubmit} loading={createMutation.isPending} disabled={!commonName} h={fullScreen ? 44 : undefined}>
            Add Livestock
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
